import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Calculator,
    Car,
    CheckSquare,
    ChevronRight,
    Download,
    FileText,
    Info,
    Loader2,
    RefreshCw,
    Search,
    Square,
} from "lucide-react";
import { toast } from "sonner";
import { yatarBedeli } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function tl(val) {
    if (val == null || val === "") return "—";
    const n = Number(val);
    if (Number.isNaN(n)) return "—";
    return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " TL";
}

const FILTRELER = [
    { id: "acik", label: "Açık dosyalar" },
    { id: "hesaplanmamis", label: "Henüz hesaplanmamış" },
    { id: "yatar", label: "Yatar işaretli" },
    { id: "hesapli", label: "Hesabı var" },
    { id: "hepsi", label: "Hepsi" },
];

export default function YatarBedeliMerkezPage() {
    const [loading, setLoading] = useState(false);
    const [hesapliyor, setHesapliyor] = useState(false);
    const [sabitler, setSabitler] = useState(null);
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [q, setQ] = useState("");
    const [filtre, setFiltre] = useState("acik");
    const [secili, setSecili] = useState(() => new Set());
    const [sonuc, setSonuc] = useState(null);
    const [makulGun, setMakulGun] = useState("");
    const [gunluk, setGunluk] = useState("");
    const [tenzil, setTenzil] = useState("10");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, d] = await Promise.all([
                sabitler ? Promise.resolve({ success: true, ...sabitler }) : yatarBedeli.sabitler(),
                yatarBedeli.dosyalar({ q, filtre }),
            ]);
            if (!sabitler && s) setSabitler(s);
            setItems(d.items || []);
            setCount(d.count || 0);
            setSecili(new Set());
        } catch (e) {
            toast.error(e?.response?.data?.error || "Liste alınamadı");
        } finally {
            setLoading(false);
        }
    }, [q, filtre, sabitler]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtre]);

    const allIds = useMemo(() => items.map((x) => x.id), [items]);
    const hepsiSecili = allIds.length > 0 && allIds.every((id) => secili.has(id));

    function toggleAll() {
        if (hepsiSecili) setSecili(new Set());
        else setSecili(new Set(allIds));
    }

    function toggle(id) {
        setSecili((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });
    }

    function overrides() {
        const o = {};
        if (makulGun) o.makul_gun = Number(makulGun);
        if (gunluk) o.gunluk_kira = Number(gunluk);
        if (tenzil !== "" && tenzil != null) o.tenzil_orani = Number(tenzil) / 100;
        return o;
    }

    async function hesapla(mode) {
        const payload = { ...overrides(), q, filtre };
        if (mode === "secili") {
            const ids = [...secili];
            if (!ids.length) {
                toast.error("Önce dosya seçin");
                return;
            }
            payload.dosya_ids = ids;
        } else {
            payload.all = true;
        }
        setHesapliyor(true);
        try {
            const r = await yatarBedeli.hesapla(payload);
            setSonuc(r);
            toast.success(`${r.ok || 0} hesap tamam · ${r.fail || 0} atlandı`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.error || "Hesaplama başarısız");
        } finally {
            setHesapliyor(false);
        }
    }

    async function pdfIndir(id, dosyaNo) {
        try {
            const blob = await yatarBedeli.pdf(id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `yatar-${dosyaNo || id}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("PDF indirilemedi");
        }
    }

    return (
        <div className="space-y-5 pb-10">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 via-[#121A2F]/90 to-slate-950 p-5 md:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 uppercase tracking-wider mb-2">
                            <Link to="/" className="hover:text-white transition">
                                Panel
                            </Link>
                            <ChevronRight className="h-3 w-3" />
                            <span className="text-slate-400">Operasyon</span>
                            <ChevronRight className="h-3 w-3" />
                            <span className="text-[#D4AF37]">Yatar Bedeli</span>
                        </div>
                        <h1 className="font-display text-2xl md:text-3xl text-white flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-black/30">
                                <Car className="h-5 w-5 text-[#D4AF37]" />
                            </span>
                            Yatar Bedeli Merkezi
                        </h1>
                        <p className="text-sm text-slate-400 mt-2 max-w-2xl leading-relaxed">
                            Araç yattı parası (mahrumiyet / ikame araç). Dosyadaki marka, model, yıl, km ve ruhsat sahibi
                            alınır; seçerek veya toplu hesaplanır. Dosya kaydında otomatik çalışmaz.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="border-white/10">
                            <RefreshCw className="h-4 w-4 mr-1" /> Yenile
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => hesapla("secili")}
                            disabled={hesapliyor || !secili.size}
                        >
                            {hesapliyor ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4 mr-1" />}
                            Seçilenleri hesapla ({secili.size})
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => hesapla("all")}
                            disabled={hesapliyor}
                            className="bg-[#D4AF37] text-black hover:bg-[#c4a030]"
                        >
                            {hesapliyor ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4 mr-1" />}
                            Filtrelenenlerin tümünü hesapla
                        </Button>
                    </div>
                </div>
            </div>

            {sabitler ? (
                <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4 text-sm text-slate-300 leading-relaxed flex gap-3">
                    <Info className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
                    <div>
                        <div className="text-[#F3E5AB] font-medium mb-1">Nasıl hesaplanır?</div>
                        {sabitler.yontem}
                        <div className="mt-2 text-xs text-slate-500">{sabitler.formul}</div>
                        <div className="mt-2 text-xs text-[#D4AF37]">{sabitler.icra_notu}</div>
                    </div>
                </div>
            ) : null}

            <div className="grid md:grid-cols-4 gap-3">
                <div className="md:col-span-2 flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                        <Input
                            className="pl-9 bg-slate-950/50 border-white/10"
                            placeholder="Dosya no, plaka, marka, müvekkil…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                        />
                    </div>
                    <Button variant="secondary" onClick={load} disabled={loading}>
                        Ara
                    </Button>
                </div>
                <label className="text-xs text-slate-400">
                    Makul gün (boş = otomatik)
                    <Input
                        className="mt-1 bg-slate-950/50 border-white/10"
                        value={makulGun}
                        onChange={(e) => setMakulGun(e.target.value)}
                        placeholder="ör. 10"
                    />
                </label>
                <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs text-slate-400">
                        Günlük kira TL
                        <Input
                            className="mt-1 bg-slate-950/50 border-white/10"
                            value={gunluk}
                            onChange={(e) => setGunluk(e.target.value)}
                            placeholder="otomatik"
                        />
                    </label>
                    <label className="text-xs text-slate-400">
                        Tenzil %
                        <Input
                            className="mt-1 bg-slate-950/50 border-white/10"
                            value={tenzil}
                            onChange={(e) => setTenzil(e.target.value)}
                        />
                    </label>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                {FILTRELER.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        onClick={() => setFiltre(f.id)}
                        className={`rounded-full px-3 py-1 text-xs border transition ${
                            filtre === f.id
                                ? "border-[#D4AF37] text-[#F3E5AB] bg-[#D4AF37]/15"
                                : "border-white/10 text-slate-400 hover:text-white"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
                <span className="text-xs text-slate-500 self-center ml-2">{count} dosya</span>
            </div>

            <div className="rounded-2xl border border-white/10 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-950/80 text-slate-400 text-xs uppercase tracking-wide">
                            <tr>
                                <th className="p-3 w-10">
                                    <button type="button" onClick={toggleAll} className="text-[#D4AF37]">
                                        {hepsiSecili ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                                    </button>
                                </th>
                                <th className="p-3 text-left">Dosya</th>
                                <th className="p-3 text-left">Müvekkil</th>
                                <th className="p-3 text-left">Araç</th>
                                <th className="p-3 text-left">Kusur</th>
                                <th className="p-3 text-right">Son hesap</th>
                                <th className="p-3 text-right">PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center text-slate-500">
                                        <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                                        Yükleniyor
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center text-slate-500">
                                        Kayıt yok
                                    </td>
                                </tr>
                            ) : (
                                items.map((d) => (
                                    <tr key={d.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                                        <td className="p-3">
                                            <button type="button" onClick={() => toggle(d.id)} className="text-[#D4AF37]">
                                                {secili.has(d.id) ? (
                                                    <CheckSquare className="h-4 w-4" />
                                                ) : (
                                                    <Square className="h-4 w-4" />
                                                )}
                                            </button>
                                        </td>
                                        <td className="p-3 text-white">
                                            <div className="font-medium">{d.dosya_no}</div>
                                            <div className="text-xs text-slate-500">{d.plaka}</div>
                                            {d.yatar_acilacak_mi ? (
                                                <span className="text-[10px] text-[#D4AF37]">yatar işaretli</span>
                                            ) : null}
                                        </td>
                                        <td className="p-3 text-slate-300">
                                            {d.muvekkil || "—"}
                                            <div className="text-[11px] text-slate-500">{d.muvekkil_tc}</div>
                                        </td>
                                        <td className="p-3 text-slate-300">
                                            {d.marka} {d.model}
                                            <div className="text-[11px] text-slate-500">
                                                {d.model_yili || "—"} · {d.km != null ? `${Number(d.km).toLocaleString("tr-TR")} km` : "km yok"}
                                                {d.eksik ? <span className="text-rose-400"> · marka/model eksik</span> : null}
                                            </div>
                                        </td>
                                        <td className="p-3 text-slate-400">%{d.kusur_orani}</td>
                                        <td className="p-3 text-right text-emerald-300">
                                            {d.son_hesap?.net_tutar != null ? tl(d.son_hesap.net_tutar) : "—"}
                                        </td>
                                        <td className="p-3 text-right">
                                            {d.son_hesap?.id ? (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => pdfIndir(d.son_hesap.id, d.dosya_no)}
                                                >
                                                    <Download className="h-4 w-4" />
                                                </Button>
                                            ) : (
                                                <FileText className="h-4 w-4 text-slate-700 inline" />
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {sonuc?.items?.length ? (
                <section className="rounded-2xl border border-white/10 bg-[#121A2F]/50 p-5">
                    <h3 className="font-display text-lg text-white mb-3">Son işlem</h3>
                    <p className="text-sm text-slate-400 mb-3">
                        {sonuc.ok} başarılı · {sonuc.fail} atlandı
                    </p>
                    <ul className="space-y-2 text-sm max-h-72 overflow-auto">
                        {sonuc.items.slice(0, 40).map((it, i) => (
                            <li key={i} className="flex justify-between gap-3 border-b border-white/5 pb-2">
                                <span className="text-slate-300">
                                    {it.dosya_no || it.dosya_id} {it.marka} {it.model}
                                    {it.error ? <span className="text-rose-400"> — {it.error}</span> : null}
                                </span>
                                {it.success ? (
                                    <span className="flex items-center gap-2 text-emerald-300">
                                        {tl(it.net_tutar)}
                                        {it.id ? (
                                            <button type="button" onClick={() => pdfIndir(it.id, it.dosya_no)}>
                                                <Download className="h-3.5 w-3.5" />
                                            </button>
                                        ) : null}
                                    </span>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}
        </div>
    );
}
